import { Box, IconButton, Menu, MenuItem, Popover, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Config, DrawAction } from './types';
import ColorLensOutlinedIcon from '@mui/icons-material/ColorLensOutlined';
import { CirclePicker } from 'react-color';
import LineWeightOutlinedIcon from '@mui/icons-material/LineWeightOutlined';
import LineStyleOutlinedIcon from '@mui/icons-material/LineStyleOutlined';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import { tooltipStyle } from './utils';

const customPallete = [
  '#162C36',
  '#004561',
  '#5891AD',
  '#2F736E',
  '#1C7685',
  '#6C6C6C',
  '#B4B4B4',
  '#51B8B0',
  '#FFA726',
  '#5E8700',
  '#FF6347',
];

const iconButtonStyles = {
  color: '#162936',
  fontSize: '1.5rem',
};
interface ConfigToolbarProps {
  config: Config;
  setConfig: React.Dispatch<React.SetStateAction<Config>>;
  drawAction: DrawAction;
}

const ConfigToolbar: React.FC<ConfigToolbarProps> = ({ config, setConfig, drawAction }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorElWeight, setAnchorElWeight] = useState<null | HTMLElement>(null);
  const [anchorElStyle, setAnchorElStyle] = useState<null | HTMLElement>(null);

  const disableStrokeWidthSelection = [DrawAction.Text];
  const disableLineStyleSelection = [DrawAction.Text, DrawAction.Scribble, DrawAction.Arrow];
  const handleOpenWeight = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElWeight(event.currentTarget);
  };
  const handleCloseWeight = () => setAnchorElWeight(null);

  const handleOpenStyle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElStyle(event.currentTarget);
  };
  const handleCloseStyle = () => setAnchorElStyle(null);

  const handleSelectWeight = (value: number) => {
    setConfig((prev) => ({ ...prev, stroke: value }));
    handleCloseWeight();
  };

  const handleSelectStyle = (value: 'solid' | 'dashed') => {
    setConfig((prev) => ({ ...prev, lineStyle: value }));
    handleCloseStyle();
  };

  const handleSelectedColor = (value: string) => {
    setConfig((prev) => ({ ...prev, color: value }));
    setTimeout(() => handleClose(), 100);
    // handleClose();
  };
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',

          backgroundColor: '#fff',
          borderRadius: '8px',
          border: '1px solid #D8DBDD',
        }}
      >
        <CustomTooltip content={'Color'} customStyle={tooltipStyle}>
          <Box
            sx={{
              cursor: 'pointer',
              border: '1px solid #D8DBDD',
            }}
          >
            <IconButton
              disableFocusRipple
              disableRipple
              disableTouchRipple
              onClick={handleClick}
              sx={{ display: 'flex', gap: '0.25rem' }}
            >
              <ColorLensOutlinedIcon sx={iconButtonStyles} />
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: config.color,
                  borderRadius: '50%',
                  border: '2px solid #ccc',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: '0 0 0 2px rgba(0,0,0,0.15)',
                  },
                }}
              />
            </IconButton>
          </Box>
        </CustomTooltip>{' '}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              p: 2,
              boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
            },
          }}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              fontWeight: 700,
              pb: '0.875rem',
              px: 1,
              color: '#162936',
            }}
          >
            Color Palette
          </Typography>
          <CirclePicker
            color={config.color}
            colors={customPallete}
            circleSize={25}
            circleSpacing={10}
            onChangeComplete={(selectedColor) => {
              handleSelectedColor(selectedColor.hex as string);
            }}
          />
        </Popover>
        {/* --- Line Weight Menu --- */}
        <CustomTooltip content={'Stroke width'} customStyle={tooltipStyle}>
          <Box
            sx={{
              cursor: 'pointer',
              border: '1px solid #D8DBDD',
            }}
          >
            <IconButton
              onClick={handleOpenWeight}
              disableFocusRipple
              disableRipple
              disableTouchRipple
              sx={iconButtonStyles}
              disabled={disableStrokeWidthSelection.includes(drawAction)}
            >
              <LineWeightOutlinedIcon />
            </IconButton>
          </Box>
        </CustomTooltip>
        <Menu
          anchorEl={anchorElWeight}
          open={Boolean(anchorElWeight)}
          onClose={handleCloseWeight}
          PaperProps={{
            sx: { p: 1, borderRadius: 2, minWidth: 160 },
          }}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              fontWeight: 700,
              pb: '0.875rem',
              px: 1,
              color: '#162936',
            }}
          >
            Line Weight
          </Typography>
          {[1, 2, 4, 8].map((thickness) => (
            <MenuItem
              key={thickness}
              onClick={() => handleSelectWeight(thickness)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                background: thickness === config.stroke ? '#2F736E1F' : '#fff',
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  height: thickness,
                  backgroundColor: '#162936',
                  border: '0.5px solid #162936 ',
                }}
              />
            </MenuItem>
          ))}
        </Menu>
        {/* --- Line Style Menu --- */}
        <CustomTooltip content={'Line style'} customStyle={tooltipStyle}>
          <Box
            sx={{
              cursor: 'pointer',
              border: '1px solid #D8DBDD',
            }}
          >
            <IconButton
              disableFocusRipple
              disableRipple
              disableTouchRipple
              onClick={handleOpenStyle}
              sx={iconButtonStyles}
              disabled={disableLineStyleSelection.includes(drawAction)}
            >
              <LineStyleOutlinedIcon />
            </IconButton>
          </Box>
        </CustomTooltip>
        <Menu
          anchorEl={anchorElStyle}
          open={Boolean(anchorElStyle)}
          onClose={handleCloseStyle}
          PaperProps={{
            sx: { p: 1, borderRadius: 2, minWidth: 160 },
          }}
        >
          <Typography
            sx={{
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              fontWeight: 700,
              pb: '0.875rem',
              px: 1,
              color: '#162936',
            }}
          >
            Line Style
          </Typography>
          {[
            { label: 'Solid', type: 'solid' },
            { label: 'Dashed', type: 'dashed' },
          ].map((item) => (
            <MenuItem
              key={item.type}
              onClick={() => handleSelectStyle(item.type as 'solid' | 'dashed')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                background: item.type === config.lineStyle ? '#2F736E1F' : '#fff',
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  height: 3,
                  borderRadius: 1,
                  borderBottom: item.type === 'dashed' ? '2px dashed #162936' : '2px solid #162936',
                }}
              />
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </>
  );
};

export default ConfigToolbar;
